/** @jsx jsx */
import { useTheme } from "@cd2/theme/build/es6"
import { IComponentProp } from "@cd2/tools/build/es6"
import { css, jsx } from "@emotion/core"
import React from "react"
import { FloatingBox } from "./FloatingBox"
import { BasicHeader } from "../../Pages/CustomerAreaNew/components/BasicHeader"
import { Spacer, useMediaQuery } from "@cd2/components"
import { brandColors } from "../EOSThemeProvider"
import { sub } from "date-fns"

export function CustomerFloatingBox({
  highlightColor,
  header,
  subheader,
  children,
  footer,
  error,
  color,
  short,
  includeSeparator = true,
  sidePaddingOverride,
  className,
  compact,
  onClick,
  footerPadding,
}: {
  highlightColor?: { top?: string; bottom?: string }
  header?: React.ReactNode | { number?: number; title: string; actions?: React.ReactNode }
  subheader?: React.ReactNode
  children?: React.ReactNode
  footer?: IComponentProp<typeof FloatingBox, "footer">
  error?: string
  color?: IComponentProp<typeof FloatingBox, "color">
  short?: boolean
  includeSeparator?: boolean
  sidePaddingOverride?: number
  className?: string
  compact?: boolean
  onClick?: () => void
  footerPadding?: string
}) {
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const theme = useTheme()
  const base = isMobile || compact ? 16 : 32
  const halfBase = base / 2
  const padding = {
    "--floating-box-footer-padding": footerPadding
      ? footerPadding
      : `${halfBase}px ${sidePaddingOverride ?? base}px ${base}px`,
    "--floating-box-header-padding": `${base}px ${sidePaddingOverride ?? base}px 0`,
    "--floating-box-child-padding": `${short ? 16 : header ? 0 : base}px ${
      sidePaddingOverride ?? base
    }px ${short || footer ? halfBase : base}px`,
  }

  if (typeof header !== "string" && !!subheader) {
    throw new Error("Subheader is not allowed when header is an object");
  }

  return (
    <FloatingBox
      onClick={onClick}
      css={css`
        ${highlightColor?.top ? `border-top: 8px solid ${highlightColor?.top};` : ""}
        ${highlightColor?.bottom ? `border-bottom: 8px solid ${highlightColor?.bottom};` : ""}
        --floating-box-child-padding: ${padding["--floating-box-child-padding"]};
        --floating-box-header-padding: ${padding["--floating-box-header-padding"]};
        --floating-box-footer-padding: ${padding["--floating-box-footer-padding"]};
        --floating-box-border-radius: 2px;
        --floating-box-border-color: transparent;
        position: relative;
        font-size: 16px;
        line-height: 1.5;
        font-weight: 200;
        b {
          font-weight: 800;
        }
        p {
          margin: 0;
        }
        ${error ? `border: 1px solid ${theme.themeColor("error")};` : ""}
      `}
      className={className}
      header={
        header && typeof header === "object" && "title" in header ? (
          <BasicHeader
            number={header.number}
            title={header.title}
            actions={header.actions}
            error={error}
          />
        ) : typeof header === "string" ? (
          <div>
            <h2
              css={css`
                font-size: 22px;
                font-weight: bold;
                margin: 0;
              `}
            >
              {header}
            </h2>
            <div
              css={css`
              border-bottom: 1px solid ${brandColors.lightNight};
              width: 100%;
              margin: 12px 0 24px;
              `}
            />
            <h3 css={css`font-weight: 200; margin: 0 0 16px; font-size: 16px`}>
              {subheader}
            </h3>
          </div>
        ) : (
          header
        )
      }
      footer={footer}
      color={color}
      includeSeparator={includeSeparator}
    >
      {children}
    </FloatingBox>
  )
}

function CustomerFloatingBoxHeader({
  children,
  actions,
}: {
  children: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        `}
      >
        {children}
        {actions}
      </div>
      <div
        css={css`
          border-bottom: 1px solid ${brandColors.lightNight};
          width: 100%;
          margin: 16px 0;
        `}
      ></div>
    </div>
  )
}
CustomerFloatingBox.Header = CustomerFloatingBoxHeader
